import { ActionIcon, Menu } from "@mantine/core";
import IconChevronDown from "@src/assets/icons/lucide/chevron-down.svg?react";
import IconHistory from "@src/assets/icons/lucide/history.svg?react";
import IconMaximize from "@src/assets/icons/lucide/maximize-2.svg?react";
import IconMessageSquare from "@src/assets/icons/lucide/message-square.svg?react";
import IconPlus from "@src/assets/icons/lucide/plus.svg?react";
import IconTerminal from "@src/assets/icons/lucide/terminal.svg?react";
import IconSidebarCollapse from "@src/assets/icons/tabler/layout-sidebar-left-expand.svg?react";
import IconUserShare from "@src/assets/icons/tabler/user-share.svg?react";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import { getShareKey } from "@src/auth/auth-utils";
import { openChatSharingModal } from "@src/components/share-whiteboard-panel/share-chat-panel-modal";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import { UserProfileContext } from "@src/components/user/user-context";
import { ChatStatus } from "@src/gql/graphql";
import clsx from "clsx";
import { useContext, useMemo } from "react";
import { isChatReadOnly } from "./chat-access";
import { sameChatId } from "./document-refs";
import { useAgentChatContext } from "./hooks/agent-chat-context";
import { useMyChats } from "./queries/chat-queries";
import { useRightDrawerTabStore } from "./right-drawer-tab-store";

type AgentChatHeaderProps = {
	layout?: "drawer" | "fullscreen";
	onClose: () => void;
	onExpand?: () => void;
	onCollapse?: () => void;
};

/** Above agent-chat-fullscreen overlay (`zIndex: 3500`). */
const CHATS_MENU_Z_INDEX = 3600;

export function AgentChatHeader({
	layout = "drawer",
	onClose,
	onExpand,
	onCollapse,
}: AgentChatHeaderProps) {
	const { activeChatId, loadChat, startNewChat } = useAgentChatContext();
	const { activeTab, setActiveTab } = useRightDrawerTabStore();
	const isElectron = Boolean(window.electronAPI?.isElectron);
	const userProfile = useContext(UserProfileContext);
	const currentUser = userProfile?.user?.[0] ?? null;
	const currentUserId = currentUser?.id ?? null;
	const shareKey = getShareKey();

	const { data: myChats } = useMyChats(undefined, {
		enabled: activeTab === "agent",
	});

	const chats = useMemo(() => {
		if (!myChats) return [];
		return myChats.filter((chat) => chat.status !== ChatStatus.Archived);
	}, [myChats]);

	const activeChat = useMemo(() => {
		if (!activeChatId || !myChats) return undefined;
		return myChats.find((chat) => sameChatId(chat.id, activeChatId));
	}, [myChats, activeChatId]);

	const canShareChat = useMemo(() => {
		if (!activeChatId) return false;
		if (isChatReadOnly(activeChat, currentUser, shareKey)) return false;
		if (
			activeChat?.ownerId &&
			currentUserId &&
			activeChat.ownerId !== currentUserId
		) {
			return false;
		}
		return true;
	}, [activeChat, activeChatId, currentUser, currentUserId, shareKey]);

	const activeChatTitle =
		activeChat?.name || (activeChatId ? "Active Chat" : "New Chat");

	return (
		<div
			className={clsx(
				"flex shrink-0 items-center justify-between gap-2 border-b border-border-subtle",
				"bg-surface-default px-3 py-1.5 sm:gap-2.5 sm:px-3 sm:py-1.5",
			)}
		>
			<div className="flex min-w-0 flex-1 items-center gap-2">
				{isElectron ? (
					<div
						role="tablist"
						aria-label="Drawer panels"
						className="flex items-center gap-0.5 bg-surface-subtle p-0.5 rounded-lg border border-border-subtle shrink-0"
					>
						<button
							type="button"
							role="tab"
							aria-selected={activeTab === "agent"}
							aria-controls="agent-chat-drawer"
							id="drawer-tab-agent"
							title="Chat"
							onClick={() => setActiveTab("agent")}
							className={clsx(
								"flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md transition-all cursor-pointer select-none",
								activeTab === "agent"
									? "bg-surface-default text-text-primary shadow-xs font-semibold"
									: "text-text-tertiary hover:text-text-secondary",
							)}
						>
							<IconMessageSquare className="w-3.5 h-3.5" />
							<span>Chat</span>
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={activeTab === "terminal"}
							aria-controls="agent-terminal-drawer"
							id="drawer-tab-terminal"
							title="Terminal"
							onClick={() => setActiveTab("terminal")}
							className={clsx(
								"flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md transition-all cursor-pointer select-none",
								activeTab === "terminal"
									? "bg-surface-default text-text-primary shadow-xs font-semibold"
									: "text-text-tertiary hover:text-text-secondary",
							)}
						>
							<IconTerminal className="w-3.5 h-3.5" />
							<span>Terminal</span>
						</button>
					</div>
				) : (
					<div className="flex h-7 shrink-0 items-center">
						<span className="truncate text-sm font-semibold leading-none text-text-primary">
							Agent
						</span>
					</div>
				)}

				{activeTab === "agent" ? (
					<div className="flex min-w-0 flex-1 items-center">
						<Menu
							shadow="md"
							width={220}
							position="bottom-start"
							withinPortal
							zIndex={CHATS_MENU_Z_INDEX}
						>
							<Menu.Target>
								<button
									type="button"
									className={clsx(
										"flex h-7 min-w-0 max-w-56 items-center gap-1 truncate",
										"rounded px-1.5 text-xs font-medium leading-none",
										"text-text-secondary hover:text-text-primary",
										"hover:bg-surface-hover-subtle cursor-pointer",
										"transition-colors",
									)}
									title={activeChatTitle}
								>
									<IconHistory className="h-3 w-3 shrink-0 text-text-tertiary" />
									<span className="truncate">{activeChatTitle}</span>
									<IconChevronDown className="h-3 w-3 shrink-0 text-text-tertiary" />
								</button>
							</Menu.Target>
							<Menu.Dropdown className="max-h-60 overflow-y-auto">
								{chats.length === 0 ? (
									<Menu.Item disabled className="text-xs">
										No previous chats
									</Menu.Item>
								) : (
									<>
										<Menu.Label>Chats ({chats.length})</Menu.Label>
										{chats.map((c) => (
											<Menu.Item
												key={c.id}
												onClick={() => void loadChat(c.id)}
												className={clsx(
													"text-xs",
													c.id === activeChatId &&
														"font-semibold text-brand-primary",
												)}
											>
												<span className="truncate block">
													{c.name || "Untitled chat"}
												</span>
											</Menu.Item>
										))}
									</>
								)}
							</Menu.Dropdown>
						</Menu>
					</div>
				) : null}
			</div>

			<div className="flex items-center gap-1 shrink-0">
				{activeTab === "agent" && activeChatId && canShareChat ? (
					<Tooltip label="Share chat">
						<ActionIcon
							variant="subtle"
							color="gray"
							size="lg"
							radius="md"
							title="Share chat"
							aria-label="Share chat"
							data-ui-id="agent-chat-share"
							className="text-text-secondary hover:text-text-primary"
							onClick={() => openChatSharingModal(activeChatId)}
						>
							<IconUserShare className="h-4 w-4" />
						</ActionIcon>
					</Tooltip>
				) : null}

				{activeTab === "agent" ? (
					<Tooltip label="New chat">
						<ActionIcon
							variant="subtle"
							color="gray"
							size="lg"
							radius="md"
							aria-label="New chat"
							className="text-text-secondary hover:text-text-primary"
							onClick={() => startNewChat()}
						>
							<IconPlus className="h-4 w-4" />
						</ActionIcon>
					</Tooltip>
				) : null}

				{layout === "drawer" && onExpand ? (
					<Tooltip label="Open fullscreen">
						<ActionIcon
							variant="subtle"
							color="gray"
							size="lg"
							radius="md"
							aria-label="Open fullscreen"
							className="text-text-secondary hover:text-text-primary"
							onClick={onExpand}
						>
							<IconMaximize className="h-4 w-4" />
						</ActionIcon>
					</Tooltip>
				) : null}

				{layout === "fullscreen" && onCollapse ? (
					<Tooltip label="Open side panel">
						<ActionIcon
							variant="subtle"
							color="gray"
							size="lg"
							radius="md"
							aria-label="Open side panel"
							className="text-text-secondary hover:text-text-primary"
							onClick={onCollapse}
						>
							<IconSidebarCollapse className="h-4 w-4" />
						</ActionIcon>
					</Tooltip>
				) : null}

				<ActionIcon
					className="hover:bg-surface-hover-subtle"
					variant="subtle"
					radius="md"
					size="lg"
					color="gray"
					title="Close"
					aria-label="Close"
					onClick={onClose}
				>
					<IconX className="h-4 w-4 sm:h-5 sm:w-5" />
				</ActionIcon>
			</div>
		</div>
	);
}
